import React, { useMemo } from 'react';
import { Modal, Button, Card, Tag, Popconfirm, Form, Input, Select, Space, Switch, InputNumber, Tooltip, Divider } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';

/**
 * 內嵌「JSON Key 顯示設定」管理元件。
 * 值結構: [{ jsonKey, displayLabel, showInList, order }]
 *
 * 行為對齊 [NodePropertyDrawer.js L7543] 的 JSON Key 卡片區，
 * 但加上 ↑ / ↓ 排序按鈕（與 FieldDisplaySettingTable 一致）。
 */
const JsonKeyDisplaySettingList = ({ value = [], onChange, t }) => {
  const sorted = useMemo(
    () => (Array.isArray(value) ? value.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) : []),
    [value]
  );

  const commit = (next) => {
    onChange?.(next.map((r, i) => ({ ...r, order: i })));
  };

  const handleChange = (index, key, val) => {
    const next = sorted.slice();
    next[index] = { ...next[index], [key]: val };
    commit(next);
  };

  const handleAdd = () => {
    const next = sorted.slice();
    next.push({ jsonKey: '', displayLabel: '', showInList: true, showInInstanceList: true, order: next.length });
    commit(next);
  };

  const handleRemove = (index) => {
    const next = sorted.filter((_, i) => i !== index);
    commit(next);
  };

  const handleMove = (index, direction) => {
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= sorted.length) return;
    const next = sorted.slice();
    [next[index], next[swap]] = [next[swap], next[index]];
    commit(next);
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        {t('processVariables.jsonKeyDisplayHelp') || '為此 JSON 變量定義要顯示的 key。每一列會在列表中顯示為一欄。'}
      </div>
      {sorted.length === 0 && (
        <div style={{ textAlign: 'center', padding: 12, color: '#999', border: '1px dashed #d9d9d9', borderRadius: 4 }}>
          {t('processVariables.noJsonKeys') || '尚未新增 JSON Key'}
        </div>
      )}
      {sorted.map((item, index) => (
        <div
          key={`jsonkey-${index}`}
          style={{
            border: '1px dashed #91d5ff',
            backgroundColor: '#e6f7ff',
            borderRadius: 4,
            padding: 8,
            marginBottom: 8
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Input
              size="small"
              placeholder={t('processVariables.jsonKeyPlaceholder') || '例如：customerName'}
              value={item.jsonKey || ''}
              onChange={(e) => handleChange(index, 'jsonKey', e.target.value)}
              style={{ flex: 1 }}
              prefix={<span style={{ color: '#999' }}>JSON Key:</span>}
            />
            <Input
              size="small"
              placeholder={t('processVariables.jsonKeyDisplayLabelPlaceholder') || '例如：客戶名稱'}
              value={item.displayLabel || ''}
              onChange={(e) => handleChange(index, 'displayLabel', e.target.value)}
              style={{ flex: 1 }}
              prefix={<span style={{ color: '#999' }}>{t('processVariables.listColumnLabel') || '列表顯示名稱:'}</span>}
            />
            <Tooltip title={t('processVariables.showInFormList') || '在表單列表中顯示'}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 36 }}>
                <span style={{ fontSize: 10, color: '#888', lineHeight: 1, whiteSpace: 'nowrap' }}>Form</span>
                <Switch
                  size="small"
                  checked={!!item.showInList}
                  onChange={(val) => handleChange(index, 'showInList', val)}
                />
              </div>
            </Tooltip>
            <Tooltip title={t('processVariables.showInInstanceList') || '在實例列表中顯示'}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 36 }}>
                <span style={{ fontSize: 10, color: '#888', lineHeight: 1, whiteSpace: 'nowrap' }}>Instance</span>
                <Switch
                  size="small"
                  checked={!!item.showInInstanceList}
                  onChange={(val) => handleChange(index, 'showInInstanceList', val)}
                />
              </div>
            </Tooltip>
            <Tooltip title={t('processVariables.moveUp') || '上移'}>
              <Button
                size="small"
                type="text"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => handleMove(index, 'up')}
              />
            </Tooltip>
            <Tooltip title={t('processVariables.moveDown') || '下移'}>
              <Button
                size="small"
                type="text"
                icon={<ArrowDownOutlined />}
                disabled={index === sorted.length - 1}
                onClick={() => handleMove(index, 'down')}
              />
            </Tooltip>
            <Button
              size="small"
              type="text"
              danger
              icon={<MinusCircleOutlined />}
              onClick={() => handleRemove(index)}
            />
          </div>
        </div>
      ))}
      <Button type="dashed" icon={<PlusOutlined />} onClick={handleAdd} style={{ width: '100%' }}>
        {t('processVariables.addJsonKey') || '新增 JSON Key'}
      </Button>
    </div>
  );
};

// 流程變量管理 Modal
const ProcessVariablesModal = ({
  visible,
  onCancel,
  processVariables,
  selectedProcessVariable,
  editingProcessVariable,
  processVariableForm,
  onAddProcessVariable,
  onEditProcessVariable,
  onDeleteProcessVariable,
  onSaveProcessVariable,
  onCancelProcessVariableEdit,
  onReorderProcessVariable,
  t
}) => {
  // 列表依 displayOrder 排序，使用者可透過 ↑ / ↓ 調整順序
  const sortedProcessVariables = useMemo(() => {
    if (!Array.isArray(processVariables)) return [];
    return processVariables
      .slice()
      .sort((a, b) => {
        const ao = a.displayOrder ?? 0;
        const bo = b.displayOrder ?? 0;
        if (ao !== bo) return ao - bo;
        return String(a.variableName || '').localeCompare(String(b.variableName || ''));
      });
  }, [processVariables]);

  return (
    <Modal
      title={t('processVariables.title')}
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={null}
      destroyOnClose
      style={{ top: 20 }}
    >
      <div style={{ display: 'flex', height: '70vh', minHeight: '600px' }}>
        {/* 左側：變量列表 */}
        <div style={{ width: '40%', borderRight: '1px solid #e8e8e8', paddingRight: '16px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <h4 style={{ margin: 0 }}>{t('processVariables.variableList')}</h4>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={onAddProcessVariable}
              size="small"
            >
              {t('processVariables.addVariable')}
            </Button>
          </div>
          
          <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
            {!sortedProcessVariables || sortedProcessVariables.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                {t('processVariables.noVariables')}
              </div>
            ) : (
              sortedProcessVariables.map((variable, index) => (
                <Card
                  key={variable.id || variable.variableName || index}
                  size="small"
                  style={{ 
                    marginBottom: '8px',
                    cursor: 'pointer',
                    border: selectedProcessVariable && selectedProcessVariable.id === variable.id ? '2px solid #1890ff' : '1px solid #d9d9d9'
                  }}
                  onClick={() => onEditProcessVariable(variable)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {variable.variableName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {variable.displayName || t('processVariables.noDisplayName')}
                      </div>
                      <Tag size="small" color="blue">
                        {variable.dataType}
                      </Tag>
                      {variable.isRequired && (
                        <Tag size="small" color="red">
                          {t('processVariables.required')}
                        </Tag>
                      )}
                      {variable.showInList && (
                        <Tag size="small" color="purple">
                          {t('processVariables.shownInFormList') || '已顯示於表單列表'}
                        </Tag>
                      )}
                      {variable.showInInstanceList && (
                        <Tag size="small" color="cyan">
                          {t('processVariables.shownInInstanceList') || '已顯示於實例列表'}
                        </Tag>
                      )}
                    </div>
                    <Space size={2} onClick={(e) => e.stopPropagation()}>
                      {/* 排序按鈕：只在未編輯時可用，避免使用者中途切換造成未存編輯遺失 */}
                      <Tooltip title={t('processVariables.moveUp') || '上移'}>
                        <Button
                          size="small"
                          type="text"
                          icon={<ArrowUpOutlined />}
                          disabled={index === 0 || !!(editingProcessVariable && !editingProcessVariable.id) || !variable.id}
                          onClick={() => onReorderProcessVariable && onReorderProcessVariable(variable.id, 'up')}
                        />
                      </Tooltip>
                      <Tooltip title={t('processVariables.moveDown') || '下移'}>
                        <Button
                          size="small"
                          type="text"
                          icon={<ArrowDownOutlined />}
                          disabled={index === sortedProcessVariables.length - 1 || !!(editingProcessVariable && !editingProcessVariable.id) || !variable.id}
                          onClick={() => onReorderProcessVariable && onReorderProcessVariable(variable.id, 'down')}
                        />
                      </Tooltip>
                      <Popconfirm
                        title={t('processVariables.confirmDelete')}
                        onConfirm={() => onDeleteProcessVariable(variable.id)}
                        okText={t('processVariables.confirmDeleteOk')}
                        cancelText={t('processVariables.confirmDeleteCancel')}
                      >
                        <Button 
                          type="text" 
                          danger 
                          size="small"
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </Space>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
        
        {/* 右側：變量編輯表單 */}
        <div style={{ width: '60%', paddingLeft: '16px', display: 'flex', flexDirection: 'column' }}>
          {editingProcessVariable ? (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
                <Form
                  form={processVariableForm}
                  layout="vertical"
                  onFinish={onSaveProcessVariable}
                >
                  <Form.Item
                    label={t('processVariables.variableName')}
                    name="variableName"
                    rules={[
                      { required: true, message: t('processVariables.variableNameRequired') },
                      { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: t('processVariables.variableNamePattern') }
                    ]}
                  >
                    <Input placeholder={t('processVariables.variableNamePlaceholder')} />
                  </Form.Item>
                  
                  <Form.Item
                    label={t('processVariables.displayName')}
                    name="displayName"
                  >
                    <Input placeholder={t('processVariables.displayNamePlaceholder')} />
                  </Form.Item>
                  
                  <Form.Item
                    label={t('processVariables.dataType')}
                    name="dataType"
                    rules={[{ required: true, message: t('processVariables.dataTypeRequired') }]}
                  >
                    <Select
                      options={[
                        { value: 'string', label: t('processVariables.dataTypeString') },
                        { value: 'int', label: t('processVariables.dataTypeInt') },
                        { value: 'decimal', label: t('processVariables.dataTypeDecimal') },
                        { value: 'datetime', label: t('processVariables.dataTypeDatetime') },
                        { value: 'boolean', label: t('processVariables.dataTypeBoolean') },
                        { value: 'text', label: t('processVariables.dataTypeText') },
                        { value: 'json', label: t('processVariables.dataTypeJson') }
                      ]}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    label={t('processVariables.description')}
                    name="description"
                  >
                    <Input.TextArea rows={2} placeholder={t('processVariables.descriptionPlaceholder')} />
                  </Form.Item>
                  
                  <Form.Item
                    label={t('processVariables.isRequired')}
                    name="isRequired"
                    valuePropName="checked"
                  >
                    <input type="checkbox" />
                  </Form.Item>
                  
                  <Form.Item
                    label={t('processVariables.defaultValue')}
                    name="defaultValue"
                  >
                    <Input placeholder={t('processVariables.defaultValuePlaceholder')} />
                  </Form.Item>
                  
                  <Form.Item
                    label={t('processVariables.validationRules')}
                    name="validationRules"
                  >
                    <Input.TextArea rows={2} placeholder={t('processVariables.validationRulesPlaceholder')} />
                  </Form.Item>
                  
                  <Form.Item
                    label={t('processVariables.jsonSchema')}
                    name="jsonSchema"
                  >
                    <Input.TextArea rows={4} placeholder={t('processVariables.jsonSchemaPlaceholder')} />
                  </Form.Item>

                  <Divider orientation="left" style={{ marginTop: 8, marginBottom: 12 }}>
                    {t('processVariables.displaySection') || '列表顯示設定'}
                  </Divider>

                  <Form.Item
                    label={t('processVariables.showInFormList') || '在表單列表中顯示'}
                    name="showInList"
                    valuePropName="checked"
                    tooltip={t('processVariables.showInFormListHelp') || '勾選後，此 PV 會出現在 PendingTasks 等表單列表的「流程資料」區塊。'}
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    label={t('processVariables.showInInstanceList') || '在實例列表中顯示'}
                    name="showInInstanceList"
                    valuePropName="checked"
                    tooltip={t('processVariables.showInInstanceListHelp') || '勾選後，此 PV 的值會顯示在流程活動看板的實例標題列，並可作為篩選條件。'}
                  >
                    <Switch />
                  </Form.Item>

                  {/* 顯示標籤 / 顯示順序：僅 dataType !== 'json' 時顯示。
                      JSON 類型的 PV 每個 JSON key 都有自己的 label 與順序（由 JSON Key 顯示設定卡片管理），
                      外層的 displayLabel/displayOrder 對 JSON PV 而言是多餘的，故隱藏避免混淆。 */}
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) => prev.dataType !== cur.dataType}
                  >
                    {() => {
                      const isJson = processVariableForm.getFieldValue('dataType') === 'json';
                      if (isJson) {
                        return (
                          <Form.Item
                            label={t('processVariables.jsonKeyDisplay') || 'JSON Key 顯示設定'}
                            name="jsonKeyDisplaySettingsList"
                            tooltip={t('processVariables.jsonKeyDisplayHelp') || '為此 JSON 變量定義要顯示的 key。每一列會在列表中顯示為一欄。順序可用列內 ↑/↓ 調整。'}
                          >
                            <JsonKeyDisplaySettingList t={t} />
                          </Form.Item>
                        );
                      }
                      return (
                        <>
                          <Form.Item
                            label={t('processVariables.displayLabel') || '顯示標籤'}
                            name="displayLabel"
                            tooltip={t('processVariables.displayLabelHelp') || '若為空，會 fallback 用 DisplayName / VariableName。'}
                          >
                            <Input placeholder={t('processVariables.displayLabelPlaceholder') || '例如：客戶總價錢'} />
                          </Form.Item>
                          <Form.Item
                            label={t('processVariables.displayOrder') || '顯示順序'}
                            name="displayOrder"
                            tooltip={t('processVariables.displayOrderHelp') || '數值越小越靠前；通常用左側列表的 ↑/↓ 即可，不必手動輸入。'}
                          >
                            <InputNumber min={0} step={1} style={{ width: 120 }} />
                          </Form.Item>
                        </>
                      );
                    }}
                  </Form.Item>
                </Form>
              </div>
              
              {/* 固定在底部的按鈕區域 */}
              <div style={{ 
                borderTop: '1px solid #e8e8e8', 
                paddingTop: '16px', 
                marginTop: '16px',
                flexShrink: 0
              }}>
                <Space>
                  <Button 
                    type="primary" 
                    onClick={() => {
                      processVariableForm.validateFields().then(values => {
                        onSaveProcessVariable(values);
                      }).catch(errorInfo => {
                        console.log('表單驗證失敗:', errorInfo);
                      });
                    }}
                  >
                    {editingProcessVariable && editingProcessVariable.id ? t('processVariables.updateVariable') : t('processVariables.createVariable')}
                  </Button>
                  <Button onClick={onCancelProcessVariableEdit}>
                    {t('processVariables.cancel')}
                  </Button>
                </Space>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {t('processVariables.selectVariableToEdit')}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProcessVariablesModal;
